import { apiClient } from "./client";
import type {
  Contact,
  ContactBulkDeleteResponse,
  ContactBulkUpdateFields,
  ContactBulkUpdateResponse,
  ContactCreate,
  ContactListResponse,
  ContactUpdate,
} from "./types";

export interface ListContactsParams {
  company_id?: number;
  is_decision_maker?: boolean;
  enrichment_status?: string;
  /** Server-side search: name, title, company, and where readable email or
   *  phone number (written any way, whole or partial, from 4 digits). */
  q?: string;
  page?: number;
  page_size?: number;
}

function buildContactParams(
  params: ListContactsParams,
): Record<string, unknown> {
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    cleaned[key] = value;
  }
  return cleaned;
}

export async function listContacts(
  slug: string,
  params: ListContactsParams = {},
): Promise<ContactListResponse> {
  const res = await apiClient.get<ContactListResponse>(
    `/projects/${slug}/contacts`,
    { params: buildContactParams(params) },
  );
  return res.data;
}

export async function exportContactsCSV(
  slug: string,
  params: ListContactsParams = {},
): Promise<void> {
  const response = await apiClient.get<Blob>(
    `/projects/${slug}/contacts/export`,
    {
      params: buildContactParams(params),
      responseType: "blob",
    },
  );

  const disposition = response.headers["content-disposition"] as
    | string
    | undefined;
  const fallback = `prism_contacts_${slug}.csv`;
  const filename = parseFilename(disposition) ?? fallback;

  const blob = new Blob([response.data], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function parseFilename(contentDisposition: string | undefined): string | null {
  if (!contentDisposition) return null;
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(contentDisposition);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ""));
    } catch {
      // fall through to legacy
    }
  }
  const legacy = /filename=("([^"]+)"|([^;]+))/i.exec(contentDisposition);
  if (legacy) return (legacy[2] ?? legacy[3] ?? "").trim() || null;
  return null;
}

export async function getContact(
  slug: string,
  id: number,
): Promise<Contact> {
  const res = await apiClient.get<Contact>(
    `/projects/${slug}/contacts/${id}`,
  );
  return res.data;
}

export async function updateContact(
  slug: string,
  id: number,
  payload: ContactUpdate,
): Promise<Contact> {
  const res = await apiClient.patch<Contact>(
    `/projects/${slug}/contacts/${id}`,
    payload,
  );
  return res.data;
}

export async function createContact(
  slug: string,
  payload: ContactCreate,
): Promise<Contact> {
  const res = await apiClient.post<Contact>(
    `/projects/${slug}/contacts`,
    payload,
  );
  return res.data;
}

export async function deleteContact(
  slug: string,
  id: number,
): Promise<void> {
  await apiClient.delete(`/projects/${slug}/contacts/${id}`);
}

export async function bulkDeleteContacts(
  slug: string,
  ids: number[],
): Promise<ContactBulkDeleteResponse> {
  const res = await apiClient.post<ContactBulkDeleteResponse>(
    `/projects/${slug}/contacts/bulk-delete`,
    { contact_ids: ids },
  );
  return res.data;
}

export async function bulkUpdateContacts(
  slug: string,
  ids: number[],
  update: ContactBulkUpdateFields,
): Promise<ContactBulkUpdateResponse> {
  const res = await apiClient.post<ContactBulkUpdateResponse>(
    `/projects/${slug}/contacts/bulk-update`,
    { contact_ids: ids, update },
  );
  return res.data;
}
